import { Add, ChevronDown } from 'Components/DesignSystem/Iconography/Icons';
import React, { useEffect, useMemo, useRef, useState } from 'react'

// Type definitions
interface CategoryType {
    id: string;
    name: string;
    description?: string;
}

interface SelectCategoryTypeProps {
    value: string;
    onChange: (categoryTypeId: string) => void;
    categoryTypes: CategoryType[];
    onCreateNew: () => void;
    placeholder?: string;
    isInvalid?: boolean;
    disabled?: boolean;
}

const SelectCategoryType: React.FC<SelectCategoryTypeProps> = ({
    value,
    onChange,
    categoryTypes,
    onCreateNew,
    placeholder = "Select Category Type",
    isInvalid = false,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                if (isOpen) {
                    setIsOpen(false);
                    setSearchTerm('');
                }
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    // Filter category types based on search term
    const filteredCategoryTypes = useMemo(() => {
        if (!searchTerm) return categoryTypes;

        return categoryTypes.filter(type =>
            type.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [categoryTypes, searchTerm]);

    // Get selected category type
    const selectedCategoryType = categoryTypes.find(type => type.id === value);

    const handleSelect = (categoryType: CategoryType) => {
        onChange(categoryType.id);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleInputFocus = () => {
        if (!isOpen && !disabled) {
            setIsOpen(true);
        }
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className="tw-relative tw-w-full" ref={dropdownRef}>
            <div className="tw-relative">
                <input
                    type="text"
                    value={isOpen ? searchTerm : (selectedCategoryType?.name || '')}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    placeholder={selectedCategoryType?.name || placeholder}
                    disabled={disabled}
                    className={`tw-w-full tw-py-2 tw-px-3 tw-pr-8 tw-border tw-rounded tw-bg-surface tw-text-sm tw-outline-none focus:tw-ring-1 focus:tw-ring-accent placeholder:tw-text-ink-secondary ${isInvalid ? 'tw-border-tone-danger-glyph' : 'tw-border-edge-strong'
                        } ${disabled ? 'tw-bg-surface-sunken tw-cursor-not-allowed tw-text-ink-disabled' : 'tw-text-ink'}`}
                    style={{
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        fontFamily: "Noto Sans",
                    }}
                    data-testid="category-type-select-input"
                />
                <button aria-label="Expand" data-testid="components-selectcategorytype-button-1"
                    onClick={toggleDropdown}
                    className={`tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-p-1 ${disabled ? 'tw-cursor-not-allowed tw-opacity-50 tw-text-ink-disabled' : 'tw-cursor-pointer tw-text-ink'
                        }`}
                    type="button"
                    disabled={disabled}
                >
                    <ChevronDown size={16} />
                </button>
            </div>

            {isOpen && !disabled && (
                <div className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-bg-surface tw-border tw-border-edge tw-rounded tw-shadow-lg tw-z-50 tw-max-h-60 tw-overflow-y-auto no-scrollbar">
                    <div className="tw-py-1 tw-my-1">
                        {/* Create New Button */}
                        <button data-testid="components-selectcategorytype-button-2"
                            onClick={() => {
                                setIsOpen(false);
                                onCreateNew();
                            }}
                            className="tw-px-2.5 tw-ms-2.5 tw-mb-2 tw-py-[5px] tw-rounded tw-text-accent tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-accent tw-inline-flex tw-justify-center tw-items-center tw-gap-1 hover:tw-bg-accent-tint tw-transition-colors"
                            type="button"
                        >
                            <Add />
                            <span className="tw-text-xs tw-font-medium tw-leading-none">
                                Create
                            </span>
                        </button>

                        {/* Category Type Options */}
                        {filteredCategoryTypes.length === 0 && searchTerm ? (
                            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-tertiary tw-italic">
                                No category types found
                            </div>
                        ) : (
                            filteredCategoryTypes.map((type) => (
                                <div role="button"
                                    key={type.id}
                                    className={`tw-px-3 tw-py-2 tw-text-ink tw-text-sm tw-leading-tight tw-cursor-pointer hover:tw-bg-surface-hover ${value === type.id ? "tw-bg-accent-tint" : ""
                                        }`}
                                    onClick={() => handleSelect(type)}
                                    data-testid={`category-type-option-${type.id}`}
                                >
                                    {type.name}
                                </div>
                            ))
                        )}

                        {filteredCategoryTypes.length === 0 && !searchTerm && (
                            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-ink-tertiary tw-italic">
                                No category types available
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelectCategoryType